/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Col, Form, message, Modal, Popconfirm, Row } from 'antd';
import { MessageSquareIcon, MessageSquareXIcon, PlusIcon, SendIcon, TrashIcon } from 'lucide-react';
import { ValuationComparing } from '../../interfaces/valuation';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import useSWR from 'swr';
import { GeneralApi, ValuationApi } from '../../apis';
import { IGeneral } from '../../interfaces';
import { RejectForm, RejectFormRef } from '../form/report/reject';
import { NumberInput, TextAreaInput, TextInput } from '../form/fields/number-input';
import { delay, currencyFormatter, currencyParser, parser, formatter } from '../../utils';

const validationSchema = Yup.object({
  comparativeVehicles: Yup.array().of(
    Yup.object({
      brandModel: Yup.string().required('Заавал оруулах'),
      manufacturedYear: Yup.number().required('Заавал оруулах'),
      importedYear: Yup.number().required('Заавал оруулах'),
      odo: Yup.number().required('Заавал оруулах'),
      price: Yup.number().required('Заавал оруулах'),
      sourceUrl: Yup.string().required('Заавал оруулах'),
    })
  ),
  priceFinal: Yup.number().required('Заавал оруулах').min(1, '1-аас их байх шаардаглатай'),
  conclusion: Yup.string()
    .required('Заавал оруулах')
    .min(1, '1-аас их байх шаардаглатай')
    .max(500, '500-аас ихгүй байх шаардаглатай'),
});

export type AppraisalFormTypes = {
  comparativeVehicles?: {
    brandModel?: string;
    manufacturedYear?: number;
    importedYear?: number;
    odo?: number;
    price?: number;
    sourceUrl?: string;
  }[];
  priceFinal?: number;
  conclusion?: string;
  hasVat?: boolean;
};

const INITIAL_COMPARATIVE_VEHICLES = {
  brandModel: undefined,
  manufacturedYear: undefined,
  importedYear: undefined,
  odo: undefined,
  price: undefined,
  sourceUrl: undefined,
};

const Appraisal = ({ payload: _payload }: { payload: ValuationComparing }) => {
  const { data: init } = useSWR<IGeneral>('/api/init', GeneralApi.init);
  const { data: payload, mutate: payloadMutate } = useSWR(
    'swr.valuation.payload',
    async () => await ValuationApi.get(_payload?.id!)
  );
  const [modal, modalContextHolder] = Modal.useModal();

  const rejectFormRef = useRef<RejectFormRef>(null);

  const [messageApi, contextHolder] = message.useMessage();

  const normalize = useCallback(() => {
    return {
      comparativeVehicles:
        (payload?.comparativeVehicles || []).length > 0
          ? payload?.comparativeVehicles
          : [INITIAL_COMPARATIVE_VEHICLES],

      priceFinal: payload?.priceFinal ?? null,
      conclusion: payload?.conclusion ?? '',
      hasVat: payload?.hasVat ?? false
    };
  }, [payload]);

  const isDisabled = useMemo(() => {
    const is = normalize();
    if (is.conclusion && is.priceFinal && (is.comparativeVehicles || []).length > 0) {
      return false;
    }
    return true;
  }, [normalize]);

  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const addComparativeVehicle = useCallback(
    (values: AppraisalFormTypes, setFieldValue: (field: string, value: any) => void | any) => {
      setFieldValue('comparativeVehicles', [
        ...(values.comparativeVehicles || []),
        INITIAL_COMPARATIVE_VEHICLES,
      ]);
    },
    []
  );

  const removeComparativeVehicle = useCallback(
    ({
      comparativeVehicles,
      index,
      setFieldValue,
    }: {
      comparativeVehicles: AppraisalFormTypes['comparativeVehicles'];
      index: number;
      setFieldValue: (field: string, value: any) => void | any;
    }) => {
      setFieldValue('comparativeVehicles', [
        ...(comparativeVehicles || []).filter((_, i) => i !== index),
      ]);
    },
    []
  );

  // Татгалзах method
  const reject = useCallback(async () => {
    modal.confirm({
      title: null,
      icon: null,
      content: (
        <RejectForm
          type="reject"
          reasons={init?.valuationRejectReasons || []}
          ref={rejectFormRef}
          onSubmit={async (values) => {
            try {
              await ValuationApi.reject(
                { reason: values.reason as string, note: values.note as string },
                payload.id
              );
              messageApi.success('Амжилттай');
              payloadMutate();
            } catch (err: any) {
              messageApi.error(err?.message);
            }
          }}
        />
      ),
      okText: 'Тийм',
      cancelText: 'Үгүй',
      onOk: async () => {
        await rejectFormRef.current?.submit();
      },
    });
  }, [rejectFormRef]);

  // Returned method
  const returned = useCallback(async () => {
    modal.confirm({
      title: null,
      icon: null,
      content: (
        <RejectForm
          type="returned"
          reasons={init?.valuationReturnReasons || []}
          ref={rejectFormRef}
          onSubmit={async (values) => {
            try {
              await ValuationApi.returned(
                { reason: values.reason as string, note: values.note as string },
                payload.id
              );
              messageApi.success('Амжилттай');
              payloadMutate();
            } catch (err: any) {
              messageApi.error(err?.message);
            }
          }}
        />
      ),
      okText: 'Тийм',
      cancelText: 'Үгүй',
      onOk: async () => {
        await rejectFormRef.current?.submit();
      },
    });
  }, [rejectFormRef]);

  // on save
  const onSubmit = useCallback(
    async (values: AppraisalFormTypes) => {
      try {
        await ValuationApi.appraise(
          {
            comparativeVehicles: values.comparativeVehicles?.map((item) => {
              return {
                ...item,
                id: undefined,
                createdAt: undefined,
                createdBy: undefined,
                updatedBy: undefined,
                updatedAt: undefined,
                deletedBy: undefined,
                deletedAt: undefined,
                valuationId: undefined,
              };
            }),
            priceFinal: values.priceFinal,
            conclusion: values.conclusion,
            hasVat: values.hasVat,
            send: false,
          } as any,
          payload.id
        );

        messageApi.success('Амжилттай');
        await delay(150);
        payloadMutate();
      } catch (err: any) {
        messageApi.error(err?.message);
      }
    },
    [messageApi, payload.id]
  );

  // on send appraisal
  const onSendAppraisal = useCallback(async () => {
    try {
      console.log('payload : ', payload);

      await ValuationApi.appraise(
        {
          comparativeVehicles: payload.comparativeVehicles?.map((item: any) => {
            return {
              brandModel: item.brandModel,
              manufacturedYear: item.manufacturedYear,
              importedYear: item.importedYear,
              odo: item.odo,
              price: item.price,
              sourceUrl: item.sourceUrl,
            };
          }),
          priceFinal: payload.priceFinal,
          conclusion: payload.conclusion,
          hasVat: payload.hasVat || false,
          send: true,
        } as any,
        payload.id
      );

      await delay(150);
      payloadMutate();
      messageApi.success('Амжилттай');
    } catch (err: any) {
      messageApi.error(err?.message);
    }

    payloadMutate();
  }, [payload]);

  /* 
  1. (appraiserId !== null) ? appraiser
  2. (valuationUserId !== branchHeadId && percentBranchHead = 0) ? valuationUser
  valuatedUserId && valuatedUser
  */

  const comparativeVehiclesRender = useCallback(
    (
      values: AppraisalFormTypes,
      setFieldValue: (field: string, value: any) => void | any,
      errors: any,
      touched: any
    ) => {
      return values.comparativeVehicles?.map((vehicle, index) => (
        <ComparativeVehicleContainer className=" pr-[0px]">
          <div className="border border-[#F1F5F9] rounded-[14px] w-[100%]">
            {values?.comparativeVehicles?.length &&
            (values?.comparativeVehicles || [])?.length > 1 ? (
              <div className=" flex  flex-row  justify-between  items-center ">
                <div className="font-manrope font-bold text-[18px] leading-[44px] tracking-[0px] text-[#282830]"></div>
                <Popconfirm
                  title="Устгах уу?"
                  description="Устгахдаа итгэлтэй байна уу!"
                  onConfirm={() =>
                    removeComparativeVehicle({
                      comparativeVehicles: values.comparativeVehicles,
                      index,
                      setFieldValue,
                    })
                  }
                  okText="Тийм"
                  cancelText="Үгүй"
                >
                  <div className="cursor-pointer hover:text-[#5869FF] hover:scale-90 transition-all duration-300 text-[#6A6A6A] bg-[#F1F5F9] rounded-[6px] p-[6px]">
                    <TrashIcon className="w-[16px] h-[16px]" color="#F5222D" />
                  </div>
                </Popconfirm>
              </div>
            ) : (
              <div className=" flex  flex-row  justify-between  items-center  px-[10px]  pt-[20px] "></div>
            )}

            <div className="p-[10px]">
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Автомашины брэнд"
                    name={`comparativeVehicles.${index}.brandModel`}
                    required
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.brandModel &&
                      touched?.comparativeVehicles?.[index]?.brandModel
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.brandModel &&
                      touched?.comparativeVehicles?.[index]?.brandModel
                        ? errors?.comparativeVehicles?.[index]?.brandModel
                        : ''
                    }
                  >
                    <TextInput
                      name={`comparativeVehicles.${index}.brandModel`}
                      placeholder="Брэнд, загвар оруулна уу"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Үйлдвэрлэсэн он*"
                    required
                    name={`comparativeVehicles.${index}.manufacturedYear`}
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.manufacturedYear &&
                      touched?.comparativeVehicles?.[index]?.manufacturedYear
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.manufacturedYear &&
                      touched?.comparativeVehicles?.[index]?.manufacturedYear
                        ? errors?.comparativeVehicles?.[index]?.manufacturedYear
                        : ''
                    }
                  >
                    <TextInput
                      name={`comparativeVehicles.${index}.manufacturedYear`}
                      placeholder="2020"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Гүйлт (км)"
                    required
                    name={`comparativeVehicles.${index}.odo`}
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.odo &&
                      touched?.comparativeVehicles?.[index]?.odo
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.odo &&
                      touched?.comparativeVehicles?.[index]?.odo
                        ? errors?.comparativeVehicles?.[index]?.odo
                        : ''
                    }
                  >
                    <NumberInput
                      name={`comparativeVehicles.${index}.odo`}
                      placeholder="45000"
                      style={{ width: '100%' }}
                      parser={parser}
                      formatter={formatter as any}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Зах зээлийн үнэ (₮)"
                    required
                    name={`comparativeVehicles.${index}.price`}
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.price &&
                      touched?.comparativeVehicles?.[index]?.price
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.price &&
                      touched?.comparativeVehicles?.[index]?.price
                        ? errors?.comparativeVehicles?.[index]?.price
                        : ''
                    }
                  >
                    <NumberInput
                      name={`comparativeVehicles.${index}.price`}
                      placeholder="68000000"
                      style={{ width: '100%' }}
                      formatter={currencyFormatter}
                      parser={currencyParser}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="Эх сурвалж"
                    required
                    name={`comparativeVehicles.${index}.sourceUrl`}
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.sourceUrl &&
                      touched?.comparativeVehicles?.[index]?.sourceUrl
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.sourceUrl &&
                      touched?.comparativeVehicles?.[index]?.sourceUrl
                        ? errors?.comparativeVehicles?.[index]?.sourceUrl
                        : ''
                    }
                  >
                    <TextInput
                      name={`comparativeVehicles.${index}.sourceUrl`}
                      placeholder="unegui.mn"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Орж ирсэн он"
                    required
                    name={`comparativeVehicles.${index}.importedYear`}
                    validateStatus={
                      errors?.comparativeVehicles?.[index]?.importedYear &&
                      touched?.comparativeVehicles?.[index]?.importedYear
                        ? 'error'
                        : ''
                    }
                    help={
                      errors?.comparativeVehicles?.[index]?.importedYear &&
                      touched?.comparativeVehicles?.[index]?.importedYear
                        ? errors?.comparativeVehicles?.[index]?.importedYear
                        : ''
                    }
                  >
                    <TextInput
                      name={`comparativeVehicles.${index}.importedYear`}
                      placeholder="2020"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>
        </ComparativeVehicleContainer>
      ));
    },
    [removeComparativeVehicle]
  );

  if ((payload && payload.status === 'RETURNED') || payload.status === 'DRAFT') {
    return null;
  }

  if (payload && payload.status === 'REJECTED') {
    return null;
  }

  if (payload.status === 'VALUATED') {
    return (
      <>
        {contextHolder}
        {modalContextHolder}
        <Formik initialValues={normalize()} validationSchema={validationSchema} onSubmit={onSubmit}>
          {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
            return (
              <Form layout="vertical" disabled>
                <div>
                  <div className="flex flex-row justify-between items-center p-[17px]">
                    <div className="font-manrope font-bold text-[20px] leading-[44px] tracking-[0px] text-[#282830]">
                      Үнэлгээчийн харьцуулсан загварын автомашинууд
                    </div>
                  </div>
                  <Card className="flex flex-row flex-1">
                    <Row>
                      {comparativeVehiclesRender(values, setFieldValue, errors, touched)}
                      <div className="p-[17px] sm:w-[580px] md:w-[580px] lg:w-[580px] xl:w-[580px] 2xl:w-[580px]">
                        <div className="border border-[#F1F5F9] rounded-[14px] p-[27px] w-[70%]">
                          <div className="font-manrope font-bold text-[18px] leading-[44px] tracking-[0px] text-[#282830] flex flex-row justify-center">
                            Үнэлгээний дүгнэлт
                          </div>
                          <div className="h-[10px]" />
                          <Row gutter={16}>
                            <Col span={24} key="priceFinal">
                              <Form.Item
                                label="Үнэлгээчний үнэлгээ"
                                required
                                name="priceFinal"
                                validateStatus={
                                  errors.priceFinal && touched.priceFinal ? 'error' : ''
                                }
                                help={
                                  errors.priceFinal && touched.priceFinal
                                    ? typeof errors.priceFinal === 'string'
                                      ? errors.priceFinal
                                      : ''
                                    : ''
                                }
                              >
                                <NumberInput
                                  name="priceFinal"
                                  placeholder="₮ 68,000,000"
                                  style={{ width: '100%' }}
                                  formatter={currencyFormatter}
                                  parser={currencyParser}
                                />
                              </Form.Item>
                            </Col>

                            <Col span={24} key="conclusion">
                              <Form.Item
                                label="Үнэлгээний дүгнэлт"
                                required
                                name="conclusion"
                                validateStatus={
                                  errors.conclusion && touched.conclusion ? 'error' : ''
                                }
                                help={
                                  errors.conclusion && touched.conclusion
                                    ? typeof errors.conclusion === 'string'
                                      ? errors.conclusion
                                      : ''
                                    : ''
                                }
                              >
                                <TextAreaInput
                                  placeholder="Машины ерөнхий байдал, гадаад төрх, дотоод засал болон бусад анхаарах зүйлсийг тэмдэглэнэ үү..."
                                  style={{ width: '100%' }}
                                  name="conclusion"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Row>
                  </Card>
                </div>
              </Form>
            );
          }}
        </Formik>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      {modalContextHolder}
      <Formik initialValues={normalize()} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical">
              <div>
                <div className="flex flex-row justify-between items-center p-[17px]">
                  <div className="font-manrope font-bold text-[20px] leading-[44px] tracking-[0px] text-[#282830]">
                    Үнэлгээчийн харьцуулсан загварын автомашинууд
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    disabled={(values?.comparativeVehicles || [])?.length >= 3}
                    onClick={() => addComparativeVehicle(values, setFieldValue)}
                    icon={<PlusIcon className="w-[20px] h-[20px]" color="white" />}
                  >
                    Ижил машин нэмэх
                  </Button>
                </div>
                <Card className="flex flex-row flex-1">
                  <Row>
                    {comparativeVehiclesRender(values, setFieldValue, errors, touched)}
                    <div className="p-[17px] sm:w-[580px] md:w-[580px] lg:w-[580px] xl:w-[580px] 2xl:w-[580px]">
                      <div className="border border-[#F1F5F9] rounded-[14px] p-[27px] w-[70%]">
                        <div className="font-manrope font-bold text-[18px] leading-[44px] tracking-[0px] text-[#282830] flex flex-row justify-center">
                          Үнэлгээний дүгнэлт
                        </div>
                        <div className="h-[10px]" />
                        <Row gutter={16}>
                          <Col span={24} key="priceFinal">
                            <Form.Item
                              label="Үнэлгээчний үнэлгээ"
                              required
                              name="priceFinal"
                              validateStatus={
                                errors.priceFinal && touched.priceFinal ? 'error' : ''
                              }
                              help={
                                errors.priceFinal && touched.priceFinal
                                  ? typeof errors.priceFinal === 'string'
                                    ? errors.priceFinal
                                    : ''
                                  : ''
                              }
                            >
                              <NumberInput
                                name="priceFinal"
                                placeholder="₮ 68,000,000"
                                style={{ width: '100%' }}
                                formatter={currencyFormatter}
                                parser={currencyParser}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={24} key="conclusion">
                            <Form.Item
                              label="Үнэлгээний дүгнэлт"
                              required
                              name="conclusion"
                              validateStatus={
                                errors.conclusion && touched.conclusion ? 'error' : ''
                              }
                              help={
                                errors.conclusion && touched.conclusion
                                  ? typeof errors.conclusion === 'string'
                                    ? errors.conclusion
                                    : ''
                                  : ''
                              }
                            >
                              <TextAreaInput
                                placeholder="Машины ерөнхий байдал, гадаад төрх, дотоод засал болон бусад анхаарах зүйлсийг тэмдэглэнэ үү..."
                                style={{ width: '100%' }}
                                name="conclusion"
                              />
                            </Form.Item>
                          </Col>
                          {/* <Col span={24} key="hasVat">
                            <Form.Item label="НӨАТ орсон эсэх" name="hasVat">
                              <Checkbox
                                name="hasVat"
                                checked={values?.hasVat}
                                onChange={(e) => setFieldValue('hasVat', e.target.checked)}
                              ></Checkbox>
                            </Form.Item>
                          </Col> */}
                        </Row>
                        <div className="flex flex-row justify-center ]">
                          <Button
                            type="primary"
                            size="large"
                            className="w-[200px]"
                            onClick={() => handleSubmit()}
                          >
                            Хадгалах
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Row>
                </Card>
              </div>
            </Form>
          );
        }}
      </Formik>

      <div className="h-[99px] w-[100%] absolute border z-[100] bottom-0 left-0 bg-[white] flex flex-row justify-end items-center pr-[21px] gap-[20px]">
        <Button
          type="default"
          size="large"
          icon={<MessageSquareIcon className="w-[20px] h-[20px]" color="black" />}
          onClick={returned}
          disabled={showAdditionalInfo}
        >
          Нэмэлт мэдээлэл шаардах
        </Button>

        <Button
          danger
          type="default"
          size="large"
          onClick={reject}
          icon={<MessageSquareXIcon className="w-[20px] h-[20px]" />}
          disabled={showAdditionalInfo}
        >
          Татгалзах
        </Button>

        <Button
          type="primary"
          size="large"
          onClick={onSendAppraisal}
          icon={<SendIcon className="w-[20px] h-[20px]" color="white" />}
          disabled={isDisabled}
        >
          Үнэлгээ илгээх
        </Button>
      </div>

      <Modal
        title="Нэмэлт мэдээлэл шаардах"
        open={showAdditionalInfo}
        onCancel={() => setShowAdditionalInfo(false)}
        footer={null}
      >
        <Form>
          <Form.Item label="Нэмэлт мэдээлэл" name="additionalInfo">
            <TextInput
              name="additionalInfo"
              placeholder="Нэмэлт мэдээлэл"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export const Card = styled.div`
  :where(.css-dev-only-do-not-override-1v28nim).ant-form-item {
    margin-bottom: 12px;
  }
`;

export const ComparativeVehicleContainer = styled.div`
  :where(.css-dev-only-do-not-override-1v28nim).ant-form-item {
    margin-bottom: 12px;
  }
`;

export { Appraisal };
